import {
	Dialog,
	Flex,
	Grid,
	Heading,
	Text,
	Tooltip,
} from '@radix-ui/themes'
import { NavLink } from '@remix-run/react'
import cn from 'clsx'
import * as React from 'react'
import { useTranslation } from 'react-i18next'

import { Payment } from '~/components/common/payment'
import { TbShoppingBag, TbTruckDelivery } from "react-icons/tb";
import { CloseButton } from '~/components/ui/close-button'
import { type GetOrder } from '~/models/ecommerce-providers/sylius/shop/fetch/order/get-order'
import { type Locale } from '~/models/locale'
import { Copyright } from '~/routes/_layout/copyright'
import { I18n } from '~/routes/_layout/i18n'
import { menu, NAVBAR_DISPLAY, NavbarMenuItem } from '~/routes/_layout/navbar'
import {
	appView,
	ROOT_I18NEXT,
	type App,
} from '~/routes/_layout/root.component'
import { SmartMedia } from '~/routes/_layout/smart-media'
import { SocialMedia } from '~/routes/_layout/social-media'
import { Image } from '~/components/ui/image.tsx'

const menuAppend: NavbarMenuItem[] = [
	{
		label: 'shipment',
		path: '/shipment',
		Icon: TbTruckDelivery,
    display: [NAVBAR_DISPLAY.sidebar],
	},
]

/* -------------------------------------------------------------------------------------------------
 * MenuPopover
 * -----------------------------------------------------------------------------------------------*/

type MenuPopoverProps = {
	app: App
	cart: GetOrder
	locale: Locale
	setApp: React.Dispatch<React.SetStateAction<App>>
}

const MenuPopover: React.FC<MenuPopoverProps> = ({ locale, setApp }) => {
	const { t } = useTranslation(ROOT_I18NEXT.routeRoot)

	return (
		<Flex direction="column" px="4" className="h-dvh bg-gray-1">
			<Flex
				direction="column"
				position="sticky"
				top="0"
				pt="5"
				pb="2"
				pr="1"
				className="z-10 bg-gray-1"
			>
				<Dialog.Close>
					<CloseButton
						onClose={() =>
							setApp((prev) => ({
								...prev,
								displaySidebar: false,
							}))
						}
					/>
				</Dialog.Close>
			</Flex>
			<Flex direction="column" gapY="4" className="grow">
				<Grid asChild columns="3" gap="1" m="auto">
					<ul className="place-content-center bg-accent-3">
						{[...menu, ...menuAppend].filter(({ display }) => display.includes(NAVBAR_DISPLAY.sidebar)).map(({ label, path, Icon }) => (
							<Flex key={label} asChild align="center" justify="center" p="6">
								<li className="group h-28 w-28 bg-gray-1">
									<NavLink
										end={path == ''}
										to={`/${locale}${path}`}
										onClick={(e) => {
											if (label === 'cart') {
												e.preventDefault()
											}
											setApp((prev) => ({
												...prev,
												displaySidebar: label === 'cart',
												sidebarView: appView.cartView,
											}))
										}}
									>
										{({ isActive }) => (
											<Flex direction="column" align="center" gapX="1">
                        <Text
                          color={isActive ? 'green' : 'gray'}
                        >
                          <Icon size="1.8em" />
                        </Text>
												<Text
													size={isActive ? '2' : '1'}
													align="center"
													color={isActive ? 'green' : 'gray'}
													weight={isActive ? 'bold' : 'light'}
												>
													{t(`label.${label}`)}
												</Text>
											</Flex>
										)}
									</NavLink>
								</li>
							</Flex>
						))}
					</ul>
				</Grid>
				<Flex
					direction="column"
					justify="center"
					align="center"
					mt="5"
					gapY="6"
					flexGrow="1"
				>
          <Image
            api={false}
            src="/pwa/img/compress/logo/logo.webp"
            alt="Brand Logo"
            width={200}
            className="-ml-2.5"
          />
					<SocialMedia />
					<Tooltip
						content={t('label.changeLanguage')}
						side="bottom"
						sideOffset={10}
					>
						<Text>
							<I18n locale={locale} />
						</Text>
					</Tooltip>
					<Flex direction="column" align="center">
						<Heading as="h4" weight="medium" mb="2" size="3">
							{t('label.paymentMethods')}
						</Heading>
						<Payment className="flex flex-wrap items-center justify-center gap-x-2 sm:justify-start" />
					</Flex>
				</Flex>
			</Flex>
			<Flex
				direction="column"
				align="center"
				gapY="3"
				bottom="0"
				mt="5"
				pb="4"
				flexShrink="0"
				className="z-10 bg-gray-1"
			>
				<Copyright showLogo={false} />
				<SmartMedia />
			</Flex>
		</Flex>
	)
}
MenuPopover.displayName = 'MenuPopover'

/* -----------------------------------------------------------------------------------------------*/

export { MenuPopover }
